"use client";

import React from "react";
import { Loader, Check, Mail } from "lucide-react";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Form, FormField, FormItem, FormLabel, FormControl, FormMessage, FormDescription } from "@/components/ui/form";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
export default function StayConnected() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll();
  return <section id="stay-connected" ref={ref} className={`dark bg-background text-foreground py-20 px-4 md:px-8 transition-opacity duration-700 ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-10"}`}>
      <div className="max-w-6xl mx-auto grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-start">
        {}
        <div className="flex flex-col justify-center h-full space-y-6">
          <h2 className="text-4xl md:text-5xl lg:text-6xl font-serif leading-tight tracking-tight">
            Never miss an insight.
          </h2>
          <p className="text-lg text-muted-foreground max-w-md leading-relaxed">
            Join our community of entrepreneurs and leaders. Get strategic
            insights, operational frameworks, and growth systems delivered
            straight to your inbox.
          </p>
          <div className="flex items-center gap-4 text-sm text-muted-foreground pt-4">
            <div className="flex items-center gap-2">
              <div className="bg-primary/10 p-1 rounded-full">
                <Check className="w-3 h-3 text-primary" />
              </div>
              <span>Weekly Strategies</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="bg-primary/10 p-1 rounded-full">
                <Check className="w-3 h-3 text-primary" />
              </div>
              <span>No Spam</span>
            </div>
          </div>
        </div>

        {}
        <div className="bg-card border border-border p-6 md:p-8 rounded-xl shadow-2xl">
          <div className="mb-6">
            <h3 className="text-xl font-semibold mb-2">Stay Connected</h3>
            <p className="text-sm text-muted-foreground">
              Customize your preferences below.
            </p>
          </div>

          <FormProvider formId="stay_connected_form_blog" sectionName="stay-connected" defaultValues={{
          name: "",
          email: "",
          interests: [],
          consent: false
        }} validationRules={{
          name: {
            required: false
          },
          email: {
            required: true,
            pattern: "email"
          },
          interests: {
            required: false
          },
          consent: {
            required: true
          }
        }} submitText="Subscribe" successMessage="Thank you for subscribing! Please check your inbox.">
            <SubscriptionForm />
          </FormProvider>
        </div>
      </div>
    </section>;
}
function SubscriptionForm() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    control
  } = useWvcForm();
  if (isSubmitted && !submitError) {
    return <div className="flex flex-col items-center justify-center py-12 text-center space-y-4 animate-in fade-in zoom-in duration-500">
        <div className="w-16 h-16 bg-green-500/10 rounded-full flex items-center justify-center">
          <Mail className="w-8 h-8 text-green-500" />
        </div>
        <h3 className="text-2xl font-serif">You're on the list!</h3>
        <p className="text-muted-foreground">{successMessage}</p>
      </div>;
  }
  const interestOptions = ["Business Strategy", "Operational Optimization", "Leadership Insights", "Growth Systems"];
  return <Form onSubmit={handleSubmit} className="space-y-6">
      {}
      <FormField control={control} name="name" render={({
      field
    }) => <FormItem>
            <FormLabel>Your Name</FormLabel>
            <FormControl>
              <Input placeholder="Jane Doe" {...field} className="bg-input/50 border-input focus:bg-background transition-colors" />
            </FormControl>
            <FormDescription>
              Optional – we’ll address you by name in our emails.
            </FormDescription>
            <FormMessage />
          </FormItem>} />

      {}
      <FormField control={control} name="email" rules={{
      required: "Email is required",
      pattern: {
        value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
        message: "Please enter a valid email address"
      }
    }} render={({
      field
    }) => <FormItem>
            <FormLabel>Email Address</FormLabel>
            <FormControl>
              <Input type="email" placeholder="jane@example.com" {...field} className="bg-input/50 border-input focus:bg-background transition-colors" />
            </FormControl>
            <FormDescription>
              We’ll never share your email with third parties.
            </FormDescription>
            <FormMessage />
          </FormItem>} />

      {}
      <FormField control={control} name="interests" render={({
      field
    }) => <FormItem>
            <FormLabel className="text-base">
              Topics You’re Interested In
            </FormLabel>
            <FormDescription className="mb-3">
              Select any topics you’d like to receive updates about.
            </FormDescription>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-2">
              {interestOptions.map((option, i) => <div key={option} data-index={i} className="flex items-start space-x-3 p-3 rounded-md border border-border bg-input/20 hover:bg-input/40 transition-colors">
                  <FormControl>
                    <Input type="checkbox" className="h-4 w-4 min-w-4 mt-1 rounded border-primary text-primary focus:ring-primary shadow-none cursor-pointer" checked={field.value?.includes(option)} onChange={e => {
              const checked = e.target.checked;
              const currentValues = field.value || [];
              if (checked) {
                field.onChange([...currentValues, option]);
              } else {
                field.onChange(currentValues.filter((val: string) => val !== option));
              }
            }} />
                  </FormControl>
                  <span className="text-sm font-medium leading-tight cursor-pointer select-none">
                    {option}
                  </span>
                </div>)}
            </div>
            <FormMessage />
          </FormItem>} />

      {}
      <FormField control={control} name="consent" rules={{
      required: "You must agree to receive emails to subscribe."
    }} render={({
      field
    }) => <FormItem className="flex flex-row items-start space-x-3 space-y-0 rounded-md p-4 border border-border bg-input/10">
            <FormControl>
              <Input type="checkbox" checked={field.value} onChange={e => field.onChange(e.target.checked)} className="h-4 w-4 min-w-4 mt-1 rounded border-primary text-primary focus:ring-primary shadow-none cursor-pointer" />
            </FormControl>
            <div className="space-y-1 leading-none">
              <FormLabel className="text-sm font-normal cursor-pointer">
                I agree to receive occasional emails from Maia Lafortezza.
              </FormLabel>
              <FormDescription>
                You can unsubscribe at any time.
              </FormDescription>
              <FormMessage />
            </div>
          </FormItem>} />

      {submitError && <div className="p-3 bg-destructive/10 border border-destructive/20 rounded-md text-sm text-destructive">
          {submitError}
        </div>}

      <Button type="submit" disabled={isSubmitting} className="w-full h-12 text-base">
        {isSubmitting ? <>
            <Loader className="mr-2 h-4 w-4 animate-spin" />
            Subscribing...
          </> : "Subscribe"}
      </Button>
    </Form>;
}